import React from "react";
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SideSheet, Chip, StatusChip, M3Button } from "../../../components/m3";
import { getOrgTokens } from "../../../theme/style/organizationPalette";
import FunctionsOutlinedIcon from "@mui/icons-material/FunctionsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";

const FORMULA_DESCRIPTIONS = {
  time_share_reduction: "N0 = base/(1-base); N1 = new/(1-new); fraction = (N0 - N1) * nonproductive_intensity / (productive_intensity + N0 * nonproductive_intensity)",
  utilization_improvement: "E0 = base/(1-base); E1 = new/(1-new); fraction = pass_through * ((E0 - E1) * intensity_unproductive) / (intensity_productive + E0 * intensity_unproductive)",
  efficiency_gain: "fraction = share_of_activity_affected * efficiency_improvement",
  activity_reduction: "fraction = share_affected * reduction * pass_through",
  switch_to_lower_emission: "fraction = scope_share * switch_share * (1 - new_to_old_emission_ratio)",
  leak_rate_reduction: "fraction = coverage * (leak_rate_base - leak_rate_new) / leak_rate_base",
  linked_flow: "fraction = weighted average of linked rows (upstream fuel cuts mirror direct fuel cuts)",
  generic_product: "fraction = product of all operating factor changes",
};

const LeverDetailSheet = ({ open, onClose, lever, category }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  if (!lever) return null;

  const inputsObj = lever.inputs || {};
  const inputEntries = Object.entries(inputsObj);

  const calcFormula = FORMULA_DESCRIPTIONS[lever.calculatorId] || FORMULA_DESCRIPTIONS.generic_product;

  return (
    <SideSheet
      open={open}
      onClose={onClose}
      title={lever.leverName || t("Operational Lever Details")}
      subtitle={`${category?.category || t("Category")} • ${category?.baselineTco2e || 0} tCO₂e Baseline`}
      width={620}
      headerExtra={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Chip size="sm" tone="primary">
            {lever.pctReductionStr || `${lever.minPct}–${lever.maxPct}%`}
          </Chip>
          <StatusChip
            size="sm"
            status={lever.feasibility === "High" ? "success" : lever.feasibility === "Medium" ? "warning" : "info"}
          >
            {`${t("Feasibility")}: ${lever.feasibility || "High"}`}
          </StatusChip>
        </Box>
      }
      footer={
        <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", gap: 1.5 }}>
          <M3Button variant="tonal" size="md" onClick={onClose}>
            {t("Close")}
          </M3Button>
        </Box>
      }
    >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1, pb: 4, textAlign: "left" }}>

          {/* Metric Highlight Card */}
          <Box
            sx={{
              p: 2.5,
              borderRadius: "12px",
              bgcolor: isDark ? "rgba(101, 157, 51, 0.08)" : "#f0fdf4",
              border: `1px solid ${isDark ? "rgba(101, 157, 51, 0.25)" : "#bbf7d0"}`,
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 2,
              textAlign: "left",
            }}
          >
                <Box sx={{ textAlign: "left" }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "12px", textAlign: "left", display: "block" }}>
                    {t("% GHG Reduction")}
                  </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "#16a34a", mt: 0.5, textAlign: "left" }}>
                      {lever.pctReductionStr}
                    </Typography>
                </Box>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "12px", textAlign: "left", display: "block" }}>
                        {t("Min Reduction")}
                      </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: tok.font.metricValue, mt: 0.5, textAlign: "left" }}>
                          {lever.minReductionTco2e} <span style={{ fontSize: "13px", fontWeight: 400 }}>tCO₂e</span>
                        </Typography>
                    </Box>
                        <Box sx={{ textAlign: "left" }}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "12px", textAlign: "left", display: "block" }}>
                            {t("Max Reduction")}
                          </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: tok.font.metricValue, mt: 0.5, textAlign: "left" }}>
                              {lever.maxReductionTco2e} <span style={{ fontSize: "13px", fontWeight: 400 }}>tCO₂e</span>
                            </Typography>
                        </Box>
                    </Box>

                    {/* Variable Change Highlight */}
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "12px",
                        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                        border: `1px solid ${tok.border.card}`,
                        textAlign: "left",
                      }}
                    >
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500, display: "block", mb: 0.5, textAlign: "left" }}>
                          {t("Operating Variable Change (Baseline → Scenario)")}
                        </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: tok.font.tableTitle, fontSize: "15px", textAlign: "left" }}>
                            {lever.variableChange || t("Standard Operational Calibration")}
                          </Typography>
                        </Box>

                        {/* Operating Variables Table */}
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                            <BuildOutlinedIcon sx={{ fontSize: 18, color: tok.font.accent }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "15px" }}>
                              {t("Operating Variables & Boundaries")}
                            </Typography>
                          </Box>
                          <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                              borderRadius: "10px",
                              border: `1px solid ${tok.border.table}`,
                              bgcolor: "transparent",
                            }}
                          >
                            <Table size="small">
                              <TableHead sx={{ bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#f1f5f9" }}>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, fontSize: "12px", textAlign: "center" }}>{t("Variable")}</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: "12px", textAlign: "center" }}>{t("Unit")}</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: "12px", textAlign: "center" }}>{t("Baseline")}</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: "12px", textAlign: "center" }}>{t("Scenario Range")}</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: "12px", textAlign: "center" }}>{t("Basis")}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {inputEntries.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={5} sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
                                      {t("Linked directly to primary fuel saving stream")}
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  inputEntries.map(([key, val]) => {
                                    const isObj = typeof val === "object" && val !== null;
                                    const unit = isObj ? val.unit || "ratio" : "ratio";
                                    const base = isObj ? (val.baseline !== undefined ? val.baseline : val.low) : val;
                                    const low = isObj ? val.low : val;
                                    const high = isObj ? val.high : val;
                                    const basis = isObj ? val.basis || "measured" : "measured";

                                    return (
                                      <TableRow key={key}>
                                        <TableCell sx={{ fontSize: "13px", fontWeight: 500, textAlign: "left" }}>
                                          {key.replace(/_/g, " ")}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: "12px", color: "text.secondary", textAlign: "left" }}>
                                          {unit}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: "13px", textAlign: "right" }}>
                                          {base}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: "13px", fontWeight: 600, textAlign: "right" }}>
                                          {low === high ? low : `${low} – ${high}`}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: "center" }}>
                                          <Chip size="sm" tone={basis === "measured" ? "success" : "neutral"}>
                                            {basis}
                                          </Chip>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>

                        {/* Mathematical Calculator Details */}
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: "12px",
                            bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                            border: `1px solid ${tok.border.card}`,
                            textAlign: "left",
                          }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, textAlign: "left" }}>
                              <FunctionsOutlinedIcon sx={{ fontSize: 18, color: tok.font.accent }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "14px", textAlign: "left" }}>
                                  {`${t("Quantification Engine")}: ${lever.calculatorId}`}
                                </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "12px",
                                p: 1.5,
                                borderRadius: "8px",
                                bgcolor: isDark ? "#18181b" : "#f1f5f9",
                                color: isDark ? "#a1a1aa" : "#334155",
                                wordBreak: "break-all",
                                lineHeight: 1.5,
                                textAlign: "left",
                              }}
                            >
                              {calcFormula}
                            </Typography>
                          </Box>

                          {/* Operational Finding & Recommended Action */}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "left" }}>
                              {lever.finding && (
                                <Box
                                  sx={{
                                    p: 2,
                                    borderRadius: "12px",
                                    bgcolor: isDark ? "rgba(14, 165, 233, 0.08)" : "#f0f9ff",
                                    border: `1px solid ${isDark ? "rgba(14, 165, 233, 0.2)" : "#bae6fd"}`,
                                    textAlign: "left",
                                  }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, textAlign: "left" }}>
                                      <InfoOutlinedIcon sx={{ fontSize: 16, color: "#0284c7" }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0369a1", fontSize: "14px", textAlign: "left" }}>
                                          {t("Operational Finding")}
                                        </Typography>
                                    </Box>
                                      <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.6, textAlign: "left" }}>
                                        {lever.finding}
                                      </Typography>
                                  </Box>
          )}

                                  {lever.action && (
                                    <Box
                                      sx={{
                                        p: 2,
                                        borderRadius: "12px",
                                        bgcolor: isDark ? "rgba(16, 185, 129, 0.08)" : "#f0fdf4",
                                        border: `1px solid ${isDark ? "rgba(16, 185, 129, 0.2)" : "#bbf7d0"}`,
                                        textAlign: "left",
                                      }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, textAlign: "left" }}>
                                          <TaskAltOutlinedIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#15803d", fontSize: "14px", textAlign: "left" }}>
                                              {t("Implementation Action")}
                                            </Typography>
                                        </Box>
                                          <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.6, textAlign: "left" }}>
                                            {lever.action}
                                          </Typography>
                                      </Box>
          )}
                                    </Box>

        {/* Data Needed to Narrow Range */}
                                  {lever.dataNeeded && lever.dataNeeded.length > 0 && (
                                        <Box sx={{ textAlign: "left" }}>
                                          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 1, textAlign: "left" }}>
                                            {t("Data Required to Narrow Uncertainty Range")}
                                          </Typography>
                                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                            {lever.dataNeeded.map((item, idx) => (
                                              <Chip key={idx} size="sm" tone="neutral">
                                                {item}
                                              </Chip>
                                            ))}
                                          </Box>
                                        </Box>
        )}

                                    </Box>
    </SideSheet>
                              );
};

                              export default LeverDetailSheet;

